import { useCallback, useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import {
  ArrowLeft, Circle, Square, Bell, Power, Keyboard, Send, Maximize2, Loader2,
  MousePointer2, Eye, EyeOff, RefreshCw,
} from 'lucide-react'
import { getMdmScreenWsUrl } from '../../../config/api'

const QUALITY_PRESETS = {
  low: { label: 'Baixa', quality: 40, maxWidth: 720, fps: 4 },
  medium: { label: 'Média', quality: 60, maxWidth: 1080, fps: 8 },
  high: { label: 'Alta', quality: 80, maxWidth: 1600, fps: 12 },
}

const LONGPRESS_MS = 500
const TAP_MAX_DIST = 0.015

const STATUS_LABEL = {
  connecting: 'Conectando...',
  waiting: 'Aguardando o tablet enviar a tela...',
  connected: 'Ao vivo',
  disconnected: 'Tablet desconectado',
  closed: 'Conexão encerrada',
  error: 'Erro na conexão',
}

/** Pedido de acesso no tablet (CONTRACT.md §12.7) — `granted` segue o fluxo normal. */
const CONSENT_LABEL = {
  asking: 'Aguardando a pessoa no tablet permitir…',
  denied: 'A pessoa no tablet recusou o acesso',
  timeout: 'Sem resposta no tablet',
}

const clamp01 = (v) => Math.min(1, Math.max(0, v))

/**
 * Tela remota do tablet (CONTRACT.md §4): recebe frames JPEG pelo WS do viewer
 * e, com "Controlar" ligado, envia toques/gestos normalizados (0..1).
 */
export default function RemoteScreen({ deviceId, initialControl = false }) {
  const canvasRef = useRef(null)
  const containerRef = useRef(null)
  const wsRef = useRef(null)
  const pointerRef = useRef(null)
  const framesRef = useRef(0)
  const decodingRef = useRef(false)

  const [status, setStatus] = useState('connecting')
  const [error, setError] = useState(null)
  const [screenInfo, setScreenInfo] = useState(null)
  const [hasFrame, setHasFrame] = useState(false)
  const [fps, setFps] = useState(0)
  const [control, setControl] = useState(initialControl)
  const [preset, setPreset] = useState('medium')
  const [text, setText] = useState('')
  const [reconnectKey, setReconnectKey] = useState(0)
  const [consent, setConsent] = useState(null) // null | asking | granted | denied | timeout
  const [ended, setEnded] = useState(false) // pessoa no tablet tocou em "Encerrar"

  // Botões "Ver tela"/"Controlar" do cabeçalho do console trocam o modo.
  useEffect(() => { setControl(initialControl) }, [initialControl])

  const send = useCallback((msg) => {
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg))
  }, [])

  // Conexão do viewer — reconecta com backoff enquanto o componente estiver montado.
  useEffect(() => {
    let disposed = false
    let retryTimer = null
    let attempt = 0

    const drawFrame = async (data) => {
      // Descarta frames enquanto o anterior ainda decodifica (evita fila crescendo).
      if (decodingRef.current) return
      decodingRef.current = true
      try {
        const bitmap = await createImageBitmap(new Blob([data], { type: 'image/jpeg' }))
        const canvas = canvasRef.current
        if (canvas && !disposed) {
          if (canvas.width !== bitmap.width || canvas.height !== bitmap.height) {
            canvas.width = bitmap.width
            canvas.height = bitmap.height
          }
          canvas.getContext('2d').drawImage(bitmap, 0, 0)
          framesRef.current += 1
          setHasFrame(true)
        }
        bitmap.close?.()
      } catch {
        /* frame corrompido — ignora */
      } finally {
        decodingRef.current = false
      }
    }

    const connect = () => {
      if (disposed) return
      let url
      try {
        url = getMdmScreenWsUrl(deviceId)
      } catch (e) {
        setStatus('error')
        setError(e.message)
        return
      }
      setStatus('connecting')
      setError(null)
      setConsent(null)
      setEnded(false)
      const ws = new WebSocket(url)
      ws.binaryType = 'arraybuffer'
      wsRef.current = ws

      ws.onopen = () => {
        attempt = 0
        setStatus('waiting')
        const p = QUALITY_PRESETS[preset]
        ws.send(JSON.stringify({ action: 'quality', quality: p.quality, maxWidth: p.maxWidth, fps: p.fps }))
      }
      ws.onmessage = (ev) => {
        if (typeof ev.data !== 'string') {
          drawFrame(ev.data)
          return
        }
        try {
          const msg = JSON.parse(ev.data)
          if (msg.status) setStatus(msg.status)
          if (msg.type === 'screen.info') setScreenInfo(msg)
          if (msg.message && msg.status === 'error') setError(msg.message)
          if (msg.type === 'screen.consent') {
            setConsent(msg.state || null)
            if (msg.state === 'granted' || msg.state === 'asking') setEnded(false)
          }
          if (msg.type === 'screen.ended') setEnded(true)
          if (msg.type === 'screen.error') setError(msg.message || 'O tablet não conseguiu capturar a tela.')
        } catch {
          /* texto não-JSON — ignora */
        }
      }
      ws.onerror = () => setStatus('error')
      ws.onclose = (ev) => {
        if (wsRef.current === ws) wsRef.current = null
        if (disposed) return
        setStatus('closed')
        if (ev.code === 1008 || ev.code === 4401 || ev.code === 4403) {
          setError('Sem permissão para ver a tela deste dispositivo.')
          return
        }
        attempt += 1
        const delay = Math.min(10000, 1000 * 2 ** Math.min(attempt, 4))
        retryTimer = setTimeout(connect, delay)
      }
    }

    connect()
    return () => {
      disposed = true
      clearTimeout(retryTimer)
      const ws = wsRef.current
      wsRef.current = null
      if (ws) ws.close()
    }
    // preset é aplicado por mensagem "quality" — não recria a conexão.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, reconnectKey])

  // Contador de FPS.
  useEffect(() => {
    const id = setInterval(() => {
      setFps(framesRef.current)
      framesRef.current = 0
    }, 1000)
    return () => clearInterval(id)
  }, [])

  const changePreset = (key) => {
    setPreset(key)
    const p = QUALITY_PRESETS[key]
    send({ action: 'quality', quality: p.quality, maxWidth: p.maxWidth, fps: p.fps })
  }

  /** Pede a tela de novo: o `quality` vira `screen.start` no agente (e o pedido aparece de novo em `ask`). */
  const requestAgain = () => {
    setConsent(null)
    setEnded(false)
    setError(null)
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      const p = QUALITY_PRESETS[preset]
      ws.send(JSON.stringify({ action: 'quality', quality: p.quality, maxWidth: p.maxWidth, fps: p.fps }))
    } else {
      setReconnectKey((k) => k + 1)
    }
  }

  const blockedMsg = ended
    ? 'A pessoa no tablet encerrou o compartilhamento'
    : consent === 'denied' || consent === 'timeout' ? CONSENT_LABEL[consent] : null
  const asking = consent === 'asking' && !ended
  const inputBlocked = !!blockedMsg || asking
  const keysDisabled = !control || inputBlocked

  const normPoint = (e) => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    return {
      x: clamp01((e.clientX - rect.left) / rect.width),
      y: clamp01((e.clientY - rect.top) / rect.height),
    }
  }

  const onPointerDown = (e) => {
    if (!control || !hasFrame || inputBlocked) return
    e.preventDefault()
    e.currentTarget.setPointerCapture?.(e.pointerId)
    pointerRef.current = { ...normPoint(e), t: performance.now() }
  }

  const onPointerUp = (e) => {
    const start = pointerRef.current
    pointerRef.current = null
    if (!control || !start) return
    e.preventDefault()
    const end = normPoint(e)
    const elapsed = performance.now() - start.t
    const dist = Math.hypot(end.x - start.x, end.y - start.y)
    if (dist < TAP_MAX_DIST) {
      send({ action: elapsed >= LONGPRESS_MS ? 'longpress' : 'tap', x: start.x, y: start.y })
    } else {
      send({
        action: 'swipe',
        x1: start.x, y1: start.y, x2: end.x, y2: end.y,
        durationMs: Math.round(Math.min(2000, Math.max(100, elapsed))),
      })
    }
  }

  const sendKey = (key) => send({ action: 'key', key })

  const sendText = (e) => {
    e.preventDefault()
    if (!text) return
    send({ action: 'text', text })
    setText('')
  }

  const fullscreen = () => {
    containerRef.current?.requestFullscreen?.().catch(() => {})
  }

  const live = (status === 'connected' || hasFrame) && !inputBlocked
  const pillLabel = blockedMsg ? (ended ? 'Encerrado no tablet' : consent === 'denied' ? 'Acesso recusado' : 'Sem resposta no tablet')
    : asking ? 'Aguardando permissão no tablet'
      : STATUS_LABEL[status] || status

  return (
    <div className="space-y-3">
      {/* Barra de controle */}
      <div className="flex flex-wrap items-center gap-2">
        <span className={clsx('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
          blockedMsg ? 'bg-amber-100 text-amber-800'
            : asking ? 'bg-blue-100 text-blue-800'
              : status === 'connected' ? 'bg-green-100 text-green-800'
                : status === 'waiting' || status === 'connecting' ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-700')}
        >
          {!blockedMsg && (asking || status === 'connecting' || status === 'waiting') && <Loader2 className="w-3 h-3 animate-spin" />}
          {pillLabel}
          {live && hasFrame && <span className="text-gray-500">· {fps} fps</span>}
        </span>

        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
          <button type="button" onClick={() => setControl(false)}
            className={clsx('flex items-center gap-1 px-3 py-1.5', !control ? 'bg-theme-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
            <Eye className="w-4 h-4" /> Ver
          </button>
          <button type="button" onClick={() => setControl(true)}
            className={clsx('flex items-center gap-1 px-3 py-1.5', control ? 'bg-theme-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50')}>
            <MousePointer2 className="w-4 h-4" /> Controlar
          </button>
        </div>

        <select value={preset} onChange={(e) => changePreset(e.target.value)}
          className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm" title="Qualidade">
          {Object.entries(QUALITY_PRESETS).map(([k, p]) => (
            <option key={k} value={k}>Qualidade {p.label}</option>
          ))}
        </select>

        <div className="flex-1" />
        {(status === 'closed' || status === 'error' || status === 'disconnected') && (
          <button type="button" onClick={() => setReconnectKey((k) => k + 1)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
            <RefreshCw className="w-4 h-4" /> Reconectar
          </button>
        )}
        <button type="button" onClick={fullscreen} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50" title="Tela cheia">
          <Maximize2 className="w-4 h-4" />
        </button>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex flex-col lg:flex-row gap-4 items-start">
        {/* Tela */}
        <div ref={containerRef} className="flex-1 flex justify-center bg-gray-900 rounded-lg p-2 min-h-[360px] w-full">
          <div className="relative flex items-center justify-center">
            <canvas
              ref={canvasRef}
              width={360}
              height={640}
              onPointerDown={onPointerDown}
              onPointerUp={onPointerUp}
              onPointerCancel={() => { pointerRef.current = null }}
              onContextMenu={(e) => e.preventDefault()}
              className={clsx('max-h-[75vh] max-w-full h-auto w-auto rounded select-none touch-none',
                control && hasFrame ? 'cursor-crosshair' : 'cursor-default',
                !hasFrame && 'opacity-0')}
            />
            {blockedMsg ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-100 text-sm text-center px-6 bg-gray-900/85 rounded">
                <EyeOff className="w-6 h-6 mb-2 text-gray-400" />
                {blockedMsg}
                <button type="button" onClick={requestAgain}
                  className="mt-3 flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-500 rounded-lg hover:bg-gray-800">
                  <RefreshCw className="w-3.5 h-3.5" /> Pedir acesso de novo
                </button>
              </div>
            ) : (!hasFrame || asking) && (
              <div className={clsx('absolute inset-0 flex flex-col items-center justify-center text-gray-400 text-sm text-center px-6 rounded',
                hasFrame && 'bg-gray-900/85')}>
                <Loader2 className="w-6 h-6 animate-spin mb-2" />
                {asking ? CONSENT_LABEL.asking : STATUS_LABEL[status] || 'Aguardando...'}
                <span className="text-xs text-gray-500 mt-2">
                  {asking
                    ? 'O pedido fica 30 s na tela do tablet. A tela aparece aqui assim que tocarem em "Permitir".'
                    : <>O tablet precisa estar online. No Android 10+ o agente confirma a captura sozinho; no Android 9, na primeira vez, marque &quot;Não mostrar novamente&quot;.</>}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Botões do Android + teclado */}
        <div className="w-full lg:w-56 space-y-3">
          <div className="grid grid-cols-4 lg:grid-cols-2 gap-2">
            <KeyButton icon={ArrowLeft} label="Voltar" disabled={keysDisabled} onClick={() => sendKey('back')} />
            <KeyButton icon={Circle} label="Início" disabled={keysDisabled} onClick={() => sendKey('home')} />
            <KeyButton icon={Square} label="Recentes" disabled={keysDisabled} onClick={() => sendKey('recents')} />
            <KeyButton icon={Bell} label="Notificações" disabled={keysDisabled} onClick={() => sendKey('notifications')} />
            <KeyButton icon={Power} label="Energia" disabled={keysDisabled} onClick={() => sendKey('power')} />
          </div>
          <form onSubmit={sendText} className="space-y-2">
            <label className="flex items-center gap-1 text-xs font-medium text-gray-600"><Keyboard className="w-3.5 h-3.5" /> Digitar no campo focado</label>
            <div className="flex gap-1">
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={keysDisabled}
                className="flex-1 min-w-0 border border-gray-300 rounded-lg px-2 py-1.5 text-sm disabled:bg-gray-50"
                placeholder="texto"
              />
              <button type="submit" disabled={keysDisabled || !text} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50" title="Enviar texto">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
          {screenInfo && (
            <p className="text-xs text-gray-500">
              Tela {screenInfo.width}×{screenInfo.height}{screenInfo.rotation ? ` · rotação ${screenInfo.rotation}°` : ''}
            </p>
          )}
          {!control && <p className="text-xs text-gray-500">Modo somente visualização. Clique em &quot;Controlar&quot; para tocar/arrastar na tela.</p>}
        </div>
      </div>
    </div>
  )
}

function KeyButton({ icon: Icon, label, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col items-center gap-1 px-2 py-2 text-xs border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-40"
      title={label}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  )
}
